import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import {
  getPaginatedEnrolmentsApi,
  getPaginatedPendingInvitationsApi,
} from 'src/ReduxStorage/InstitutionAdminSettings/asyncActions';
import { CourseEnrolment } from 'src/ReduxStorage/InstitutionAdminSettings/types';
import {
  INSTITUTION,
  SEARCH,
  PORTAL,
  BACKGROUND_TASK,
  COURSE,
} from 'src/ReduxStorage/resourceNames';
import {
  BackgroundTaskResource,
  BackgroundTaskResourceDeleteBackgroundTask,
} from 'src/resource/BackgroundTaskResource/types';
import {
  CourseResource,
  CourseResourceDefaultCohortPath,
  CourseResourceGetAllValidCohorts,
} from 'src/resource/CourseResource/types';
import {
  InstitutionResource,
  InstitutionResourceGetInstitutionCohortsByCoursePath,
} from 'src/resource/Institution/types';
import {
  PortalResource,
  PortalResourceAddEnrolmentsToUserGroups,
  PortalResourceGetCourseSummaries,
  PortalResourceGetUserGroups,
  PortalResourceRemoveEnrolments,
  PortalResourceUpdateRoleForUsers,
  PortalResourceResendInvitationEmails,
  PortalResourceScheduleAssignToCourse,
  PortalResourceRemoveFromCourse,
  ScheduleAssignToCourseFilter,
  PortalResourceScheduleAssignInvitedUsersToCourse,
  ScheduleAssignInvitedUsersToCourseFilter,
  PortalResourceScheduleInviteMembers,
  PortalResourceGetPaginatedEnrolments,
  PortalResourceGetPaginatedPendingInvitations,
  PortalResourceGetBackgroundTask,
} from 'src/resource/PortalResource/types';
import { ResourceApi } from 'src/resource/Resource';
import {
  SearchResource,
  SearchResourceSuggestUser,
  SearchResourceSuggestUserReturnResult,
} from 'src/resource/SearchResource/types';
import { axiosInstance } from 'src/web/utilities/axios';
import { AssignedCourseRolesType, CourseRolesPayloadType } from './types';

export const useSuggestUserApi = (
  initialSearchQuery = '',
  institutionPath: string,
  filterPublicInstitutions = false // assign to true if you want to filter the query per institution
): [
  null | SearchResourceSuggestUserReturnResult[],
  (searchQuery: string) => void,
  () => void
] => {
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [queryResult, setQueryResult] = useState<
    null | SearchResourceSuggestUserReturnResult[]
  >(null);

  const debouncedSuggestUserApiCall = useCallback(
    debounce((searchQuery: string, startIndex: number) => {
      // Do the exact api calll
      const params = {
        types: 'user' as const,
        query: searchQuery,
        institutionPaths: [institutionPath],
        start: startIndex,
        filterPublicInstitutions,
      };
      return ResourceApi.lookup<SearchResource, SearchResourceSuggestUser>(
        SEARCH,
        'suggestUser',
        params
      ).then(({ results }) => {
        setQueryResult(results);
      });
    }, 300),
    [institutionPath, filterPublicInstitutions, setQueryResult]
  );

  const loadMore = useCallback((): void => {
    debouncedSuggestUserApiCall(searchQuery, (queryResult?.length ?? 0) + 1);
  }, [searchQuery, queryResult]);

  useEffect(() => {
    // Do the Api call everytime search query change
    if (searchQuery !== '') {
      debouncedSuggestUserApiCall(searchQuery, 0);
    } else {
      setQueryResult(null);
    }
  }, [searchQuery]);

  return [queryResult, setSearchQuery, loadMore];
};

export const useGetCoursesInformation = (
  institutionPath: string,
  coursePaths: string[],
  pageSize = 100
): [CourseEnrolment[], boolean, () => void] => {
  const [pageIndex, setPageIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [coursesData, setCoursesData] = useState<CourseEnrolment[]>([]);

  useEffect(() => {
    if (pageSize > 0) {
      const startLookupCoursePathsIndex = pageIndex * pageSize;
      setIsLoading(true);
      ResourceApi.read<PortalResource, PortalResourceGetCourseSummaries>(
        PORTAL,
        institutionPath,
        'getCourseSummaries',
        {
          coursePaths: coursePaths.slice(
            startLookupCoursePathsIndex,
            startLookupCoursePathsIndex + pageSize
          ),
        }
      ).then((res) => {
        setCoursesData((prev) => [...prev, ...Object.values(res)]);
        setIsLoading(false);
      });
    }
  }, [pageIndex, pageSize]);

  const loadMore = useCallback(() => setPageIndex((prev) => prev + 1), [
    setPageIndex,
  ]);

  return [coursesData, isLoading, loadMore];
};

export const useGetPortalGroupsApi = (
  institutionPath: string
): [PortalResourceGetUserGroups['return']] => {
  const [data, setData] = useState<string[]>([]);
  useEffect(() => {
    ResourceApi.read<PortalResource, PortalResourceGetUserGroups>(
      PORTAL,
      institutionPath,
      'getUserGroups',
      {}
    ).then((res) => {
      setData(res);
    });
  }, [institutionPath]);
  return [data];
};

export const useAddEnrolmentsToUserGroupsApi = (
  institutionPath: string
): {
  addEnrolmentsToUserGroups: (params: {
    userGroups: string[];
    ids: string[];
  }) => Promise<unknown>;
} => {
  const addEnrolmentsToUserGroups = useCallback(
    ({ userGroups, ids }: { userGroups: string[]; ids: string[] }) => {
      return ResourceApi.read<
        PortalResource,
        PortalResourceAddEnrolmentsToUserGroups
      >(PORTAL, institutionPath, 'addEnrolmentsToUserGroups', {
        userGroups,
        enrolmentIds: ids,
      });
    },
    [institutionPath]
  );

  return { addEnrolmentsToUserGroups };
};

export const useRemoveEnrolmentsApi = (
  institutionPath: string
): {
  removeEnrolments: (params: { ids: string[] }) => Promise<unknown>;
} => {
  const removeEnrolments = useCallback(
    ({ ids }: { ids: string[] }) => {
      return ResourceApi.update<PortalResource, PortalResourceRemoveEnrolments>(
        PORTAL,
        institutionPath,
        'removeEnrolments',
        {
          enrolmentIds: ids,
        }
      );
    },
    [institutionPath]
  );

  return { removeEnrolments };
};

export const useUpdateRoleForUsersApi = (
  institutionPath: string
): {
  updateRoleForUsers: (params: {
    ids: string[];
    role: 'staff' | 'admin';
  }) => Promise<unknown>;
} => {
  const updateRoleForUsers = useCallback(
    ({ ids, role }: { ids: string[]; role: 'staff' | 'admin' }) => {
      return ResourceApi.update<
        PortalResource,
        PortalResourceUpdateRoleForUsers
      >(PORTAL, institutionPath, 'updateRoleForUsers', {
        enrolmentIds: ids,
        role,
      });
    },
    [institutionPath]
  );
  return { updateRoleForUsers };
};

export const useResendInvitationEmailsApi = (
  institutionPath: string
): {
  resendInvitationEmails: (params: { ids: string[] }) => Promise<unknown>;
} => {
  const resendInvitationEmails = useCallback(
    ({ ids }: { ids: string[] }) => {
      return ResourceApi.update<
        PortalResource,
        PortalResourceResendInvitationEmails
      >(PORTAL, institutionPath, 'resendInvitationEmails', {
        invitedUserIds: ids,
      });
    },
    [institutionPath]
  );

  return { resendInvitationEmails };
};

export const useUninvitePendingTeamMembersApi = (
  institutionPath: string
): {
  uninvitePendingTeamMembers: (params: { ids: string[] }) => Promise<unknown>;
} => {
  const uninvitePendingTeamMembers = useCallback(
    ({ ids }: { ids: string[] }) => {
      const formData = new FormData();
      formData.append('email', ids.join(','));
      return axiosInstance.post(
        `/api/institution/uninvite/?institution=${institutionPath}`,
        formData
      );
    },
    [institutionPath]
  );
  return { uninvitePendingTeamMembers };
};

export const useGetInstitutionCohortsByCoursePathApi = (
  institutionPath: string,
  coursePath: string
): {
  cohortsData:
    | InstitutionResourceGetInstitutionCohortsByCoursePath['return']
    | null;
  isLoading: boolean;
} => {
  const [cohortsData, setCohortsData] = useState<
    InstitutionResourceGetInstitutionCohortsByCoursePath['return'] | null
  >(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    setIsLoading(true);
    ResourceApi.read<
      InstitutionResource,
      InstitutionResourceGetInstitutionCohortsByCoursePath
    >(INSTITUTION, institutionPath, 'getInstitutionCohortsByCoursePath', {
      coursePathList: [coursePath],
    })
      .then(setCohortsData)
      .finally(() => setIsLoading(false));
  }, [institutionPath, coursePath]);
  return { cohortsData, isLoading };
};

type CohortData = {
  path: string;
  name: string;
  numEnrolled?: number;
  defaultCohort?: boolean;
};

export const useGetAllValidCohortsApi = (
  institutionPath: string,
  coursePath: string
): { cohortsData: CohortData[]; isLoading: boolean } => {
  const [cohortsData, setCohortsData] = useState<CohortData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // need to call this hook to get numEnrolled in cohorts
  const {
    cohortsData: institutionCohorts,
  } = useGetInstitutionCohortsByCoursePathApi(institutionPath, coursePath);

  useEffect(() => {
    setIsLoading(true);

    if (institutionCohorts) {
      const [coursesData] = institutionCohorts.courses;
      const allCohortsData: {
        [cohortPath: string]: CohortData;
      } = {};
      coursesData.classes.forEach((cohort) => {
        allCohortsData[cohort.path] = cohort;
      });

      // next retrieve all non-closed cohorts, and the defaultCohortPath
      ResourceApi.read<
        CourseResource,
        CourseResourceGetAllValidCohorts,
        CourseResourceDefaultCohortPath
      >(COURSE, coursePath, [
        {
          method: 'getAllValidCohorts',
          args: {},
        },
        { method: 'defaultCohortPath', args: {} },
      ])
        .then(([allValidCohorts, defaultCohortPath]) => {
          // filters out closed cohorts from allCohortsData, and mark the defaultCohort
          const newState: typeof cohortsData = allValidCohorts.map((cohort) => {
            const currentCohort = allCohortsData[cohort.path];
            if (defaultCohortPath === cohort.path)
              // current cohort is a default cohort
              currentCohort.defaultCohort = true;
            return currentCohort;
          });

          setCohortsData(newState);
        })
        .finally(() => setIsLoading(false));
    }
  }, [institutionPath, coursePath, institutionCohorts]);

  return { cohortsData, isLoading };
};

export const useScheduleAssignToCourseApi = (
  institutionPath: string,
  assignStaff: boolean
): {
  scheduleAssignToCourse: (params: {
    ids: string[];
    courseRoles: AssignedCourseRolesType;
    totalAssignedUsers: number;
    filterParams?: ScheduleAssignToCourseFilter;
  }) => Promise<unknown>;
} => {
  const scheduleAssignToCourse = useCallback(
    ({
      ids,
      courseRoles,
      totalAssignedUsers,
      filterParams,
    }: {
      ids: string[];
      courseRoles: AssignedCourseRolesType;
      totalAssignedUsers: number;
      filterParams?: ScheduleAssignToCourseFilter;
    }) => {
      const courseRolesPayload: CourseRolesPayloadType = [];
      Object.entries(courseRoles).forEach(([coursePath, courseRole]) => {
        const { role } = courseRole;
        if (role === 'admin') {
          courseRolesPayload.push({
            role,
            course: coursePath,
          });
        } else if (role === 'staff' || role === 'user') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const { cohortRoles } = courseRole;
          Object.entries(cohortRoles).forEach(([cohortPath, cohortRole]) => {
            if (role === 'staff') {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const roles = Object.keys(cohortRole)
                .map((roleType) =>
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  cohortRole[roleType as keyof typeof cohortRole]
                    ? roleType
                    : ''
                )
                .filter((role) => role);
              courseRolesPayload.push({
                role,
                cohort: cohortPath,
                roles: roles as (
                  | 'facilitator'
                  | 'assessor'
                  | 'assessment_designer'
                )[],
              });
            } else {
              courseRolesPayload.push({
                role,
                cohort: cohortPath,
              });
            }
          });
        }
      });

      return ResourceApi.update<
        PortalResource,
        PortalResourceScheduleAssignToCourse
      >(PORTAL, institutionPath, 'scheduleAssignToCourse', {
        userIds: ids,
        courseRoles: courseRolesPayload,
        assignStaff,
        totalAssignedUsers,
        ...filterParams,
      });
    },
    [institutionPath, assignStaff]
  );

  return { scheduleAssignToCourse };
};

export const useRemoveFromCourseApi = (
  institutionPath: string
): {
  removeFromCourse: (param: {
    ids: string[];
    coursePaths: string[];
  }) => Promise<unknown[]>;
} => {
  const removeFromCourse = useCallback(
    ({ ids, coursePaths }: { ids: string[]; coursePaths: string[] }) => {
      return Promise.all(
        coursePaths.map((coursePath) =>
          ResourceApi.update<PortalResource, PortalResourceRemoveFromCourse>(
            PORTAL,
            institutionPath,
            'removeFromCourse',
            { userIds: ids, coursePath }
          )
        )
      );
    },
    [institutionPath]
  );
  return { removeFromCourse };
};

export const useScheduleAssignInvitedUsersToCourse = (
  institutionPath: string,
  assignStaff: boolean
): {
  scheduleAssignInvitedUsersToCourse: (param: {
    ids: string[];
    courseRoles: AssignedCourseRolesType;
    totalAssignedUsers: number;
    filterParams?: ScheduleAssignInvitedUsersToCourseFilter;
  }) => Promise<unknown>;
} => {
  const scheduleAssignInvitedUsersToCourse = useCallback(
    ({
      ids,
      courseRoles,
      totalAssignedUsers,
      filterParams,
    }: {
      ids: string[];
      courseRoles: AssignedCourseRolesType;
      totalAssignedUsers: number;

      filterParams?: ScheduleAssignInvitedUsersToCourseFilter;
    }) => {
      const courseRolesPayload: CourseRolesPayloadType = [];
      Object.keys(courseRoles).forEach((coursePath) => {
        const current = courseRoles[coursePath];
        const { role } = current;
        if (role === 'admin') {
          courseRolesPayload.push({
            role,
            course: coursePath,
          });
        } else if (role === 'staff' || role === 'user') {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const { cohortRoles } = current;
          Object.entries(cohortRoles).forEach(([cohortPath, cohortRole]) => {
            if (role === 'staff') {
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              const roles = Object.keys(cohortRole)
                .map((roleType) =>
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  cohortRole[roleType as keyof typeof cohortRole]
                    ? roleType
                    : ''
                )
                .filter((role) => role);
              courseRolesPayload.push({
                role,
                cohort: cohortPath,
                roles: roles as (
                  | 'facilitator'
                  | 'assessor'
                  | 'assessment_designer'
                )[],
              });
            } else {
              courseRolesPayload.push({
                role,
                cohort: cohortPath,
              });
            }
          });
        }
      });

      return ResourceApi.update<
        PortalResource,
        PortalResourceScheduleAssignInvitedUsersToCourse
      >(PORTAL, institutionPath, 'scheduleAssignInvitedUsersToCourse', {
        userIds: ids,
        courseRoles: courseRolesPayload,
        assignStaff,
        totalAssignedUsers,
        ...filterParams,
      });
    },
    [institutionPath, assignStaff]
  );

  return { scheduleAssignInvitedUsersToCourse };
};

export const useScheduleInviteMembersApi = (
  institutionPath: string
): {
  scheduleInviteMembers: (
    params: PortalResourceScheduleInviteMembers['args']
  ) => Promise<unknown>;
  error: string;
  isLoading: boolean;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const scheduleInviteMembers = useCallback(
    async (params: PortalResourceScheduleInviteMembers['args']) => {
      setIsLoading(true);
      try {
        await ResourceApi.update<
          PortalResource,
          PortalResourceScheduleInviteMembers
        >(PORTAL, institutionPath, 'scheduleInviteMembers', params);
        setError('');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        setError(err?.response?.reason ?? err.message);
        throw err;
      }
      setIsLoading(false);
    },
    [institutionPath]
  );

  return { scheduleInviteMembers, error, isLoading };
};

export const useGetBackgroundTasksApi = (
  institutionPath: string
): {
  isLoading: boolean;
  getBackgroundTasks: (
    params: PortalResourceGetBackgroundTask['args']
  ) => Promise<PortalResourceGetBackgroundTask['return']>;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const getBackgroundTasks = useCallback(
    (
      params: PortalResourceGetBackgroundTask['args']
    ): Promise<PortalResourceGetBackgroundTask['return']> => {
      setIsLoading(true);
      return ResourceApi.read<PortalResource, PortalResourceGetBackgroundTask>(
        PORTAL,
        institutionPath,
        'getBackgroundTasks',
        params
      ).finally(() => {
        setIsLoading(false);
      });
    },
    [institutionPath]
  );
  return { getBackgroundTasks, isLoading };
};

export const useDeleteBackgroundTaskApi = (): {
  deleteBackgroundTask: (taskId: string) => Promise<unknown>;
} => {
  const deleteBackgroundTask = (taskId: string): Promise<unknown> => {
    return ResourceApi.update<
      BackgroundTaskResource,
      BackgroundTaskResourceDeleteBackgroundTask
    >(BACKGROUND_TASK, taskId, [{ method: 'delete', args: {} }]);
  };
  return { deleteBackgroundTask };
};

export const useGetPaginatedEnrolmentsApi = (
  institutionPath: string,
  initArgs?: PortalResourceGetPaginatedEnrolments['args']
): {
  data: PortalResourceGetPaginatedEnrolments['return'];
  error: string;
  isLoading: boolean;
  getPaginatedEnrolments: (
    args: PortalResourceGetPaginatedEnrolments['args']
  ) => Promise<void>;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<
    PortalResourceGetPaginatedEnrolments['return']
  >({
    enrolments: [],
    hasNextPage: false,
    hasPrevPage: false,
    total: 0,
    totalPages: 0,
  });
  const [error, setError] = useState('');

  const getPaginatedEnrolments = (
    args: PortalResourceGetPaginatedEnrolments['args']
  ): Promise<void> => {
    setIsLoading(true);
    setError('');
    return getPaginatedEnrolmentsApi(institutionPath, args)
      .then((res) => {
        setData(res);
      })
      .catch(() => {
        setError('Failed to fetch enrolments data!');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (initArgs) {
      getPaginatedEnrolments(initArgs);
    }
  }, []);

  return { data, error, isLoading, getPaginatedEnrolments };
};

export const useGetPaginatedPendingInvitations = (
  institutionPath: string,
  initArgs?: PortalResourceGetPaginatedPendingInvitations['args']
): {
  data: PortalResourceGetPaginatedPendingInvitations['return'];
  error: string;
  isLoading: boolean;
  getPaginatedPendingInvitations: (
    args: PortalResourceGetPaginatedPendingInvitations['args']
  ) => Promise<void>;
} => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<
    PortalResourceGetPaginatedPendingInvitations['return']
  >({
    hasNextPage: false,
    invitedUsers: [],
    total: 0,
  });
  const [error, setError] = useState('');

  const getPaginatedPendingInvitations = (
    args: PortalResourceGetPaginatedPendingInvitations['args']
  ): Promise<void> => {
    setIsLoading(true);
    setError('');
    return getPaginatedPendingInvitationsApi(institutionPath, args)
      .then((res) => {
        setData(res);
      })
      .catch(() => {
        setError('Failed to fetch pending invitations data!');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (initArgs) {
      getPaginatedPendingInvitations(initArgs);
    }
  }, []);

  return { data, error, isLoading, getPaginatedPendingInvitations };
};
